import './Promotions.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../firebase/config';

const CATEGORIES = [
    'All Products',
    'Clothing',
    'Accessories',
    'Printed Materials',
    'Promotional Materials',
    'Packaging & Display',
    'Full Sublimation',
];

function formatEndDate(ts) {
    if (!ts) return null;
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });
}

function isPromotionCurrentlyAvailable(promotion, now = new Date()) {
    if (!promotion) return false;

    const status = String(promotion.status || '').toLowerCase();
    if (!['active', 'scheduled'].includes(status)) return false;

    const startDate = promotion.startDate?.toDate?.() || (promotion.startDate ? new Date(promotion.startDate) : null);
    const endDate = promotion.endDate?.toDate?.() || (promotion.endDate ? new Date(promotion.endDate) : null);

    if (startDate instanceof Date && !Number.isNaN(startDate.getTime()) && now < startDate) return false;
    if (endDate instanceof Date && !Number.isNaN(endDate.getTime()) && now > endDate) return false;

    return true;
}

function normalizeText(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

const Promotions = () => {
    const navigate = useNavigate();

    const [promotions, setPromotions] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All Products');
    const [copiedKey, setCopiedKey] = useState(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const sliderRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        Promise.all([
            getDocs(query(collection(db, 'promotions'))),
            getDocs(query(collection(db, 'products'))),
            getDocs(query(collection(db, 'categories'))),
        ])
            .then(([promotionSnap, productSnap, categorySnap]) => {
                if (!mounted) return;

                const now = new Date();
                setPromotions(
                    promotionSnap.docs
                        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
                        .filter((promotion) => isPromotionCurrentlyAvailable(promotion, now))
                );
                setProducts(productSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
                setCategories(categorySnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
                setLoading(false);
            })
            .catch((err) => {
                if (!mounted) return;
                console.error('Error fetching promotions:', err);
                setError('Could not load promotions. Please try again later.');
                setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const promoCards = promotions.filter((promotion) => promotion.promoCode);

    const promotionLookup = useMemo(() => {
        const categoryToSubcategories = new Map();
        const subcategoryToCategory = new Map();
        const productToCategory = new Map();

        categories.forEach((category) => {
            const categoryName = normalizeText(category.name);
            if (!categoryName) return;

            if (!categoryToSubcategories.has(categoryName)) {
                categoryToSubcategories.set(categoryName, new Set());
            }

            (Array.isArray(category.subcategories) ? category.subcategories : []).forEach((subcategory) => {
                const subcategoryName = normalizeText(subcategory);
                if (!subcategoryName) return;
                categoryToSubcategories.get(categoryName).add(subcategoryName);
                if (!subcategoryToCategory.has(subcategoryName)) {
                    subcategoryToCategory.set(subcategoryName, categoryName);
                }
            });
        });

        products.forEach((product) => {
            const productName = normalizeText(product.name);
            const categoryName = normalizeText(product.category);
            const subcategoryName = normalizeText(product.subcategory || product.subCategory);

            if (productName && categoryName) {
                productToCategory.set(productName, categoryName);
            }

            if (!categoryName) return;

            if (!categoryToSubcategories.has(categoryName)) {
                categoryToSubcategories.set(categoryName, new Set());
            }

            if (subcategoryName) {
                categoryToSubcategories.get(categoryName).add(subcategoryName);
                subcategoryToCategory.set(subcategoryName, categoryName);
            }
        });

        return { categoryToSubcategories, subcategoryToCategory, productToCategory };
    }, [categories, products]);

    const categoryDeals = useMemo(() => {
        if (activeCategory === 'All Products') return promotions;

        const activeCategoryKey = normalizeText(activeCategory);
        const matchingSubcategories = promotionLookup.categoryToSubcategories.get(activeCategoryKey) || new Set();

        return promotions.filter((promotion) => {
            const target = normalizeText(promotion.discountedProducts);

            if (!target || target === 'all products') return true;
            if (target === activeCategoryKey) return true;
            if (matchingSubcategories.has(target)) return true;
            if (promotionLookup.subcategoryToCategory.get(target) === activeCategoryKey) return true;
            if (promotionLookup.productToCategory.get(target) === activeCategoryKey) return true;

            return false;
        });
    }, [activeCategory, promotionLookup, promotions]);

    function handleCopy(key, code) {
        if (!code) return;

        navigator.clipboard.writeText(code).then(() => {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    }

    function handleDotClick(idx) {
        if (!sliderRef.current) return;
        const slider = sliderRef.current;
        const card = slider.querySelector('.promo-carousel-slide');
        if (!card) return;

        slider.scrollTo({ left: (card.offsetWidth + 12) * idx, behavior: 'smooth' });
        setActiveSlide(idx);
    }

    function handleSliderScroll() {
        if (!sliderRef.current) return;
        const slider = sliderRef.current;
        const card = slider.querySelector('.promo-carousel-slide');
        if (!card) return;

        const idx = Math.round(slider.scrollLeft / (card.offsetWidth + 12));
        setActiveSlide(Math.min(idx, promoCards.length - 1));
    }

    if (loading) {
        return (
            <div className="promotions-container">
                <div className="promos-page-header">
                    <h1 className="promos-page-title">JG Promos &amp; Discounts</h1>
                    <p className="promos-page-subtitle">
                        Exclusive discount codes and category deals - updated regularly.
                    </p>
                </div>
                <div className="promos-loading">
                    <div className="promos-spinner" />
                    <span>Loading promotions...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="promotions-container">
            <div className="promos-page-header">
                <h1 className="promos-page-title">JG Promos &amp; Discounts</h1>
                <p className="promos-page-subtitle">
                    Exclusive discount codes and category deals - updated regularly.
                </p>
            </div>

            {error && <p className="promos-error">{error}</p>}

            <section className="promos-section">
                <h2 className="promos-section-title">Active Promo Codes</h2>
                <p className="promos-section-sub">
                    Copy a code and apply it at checkout to enjoy your discount.
                </p>

                {!error && promoCards.length === 0 && (
                    <div className="promos-empty">
                        <NoDealsIcon />
                        <p>No active promo codes right now. Check back soon!</p>
                    </div>
                )}

                {promoCards.length > 0 && (
                    <div className="promo-cards-grid">
                        {promoCards.map((promo) => {
                            const key = `promo-${promo.id}`;
                            return (
                                <div key={promo.id} className="promo-card-wrapper">
                                    <div className="promo-card">
                                        {promo.imageUrl
                                            ? <img src={promo.imageUrl} alt={promo.promotionName} className="promo-card-image" />
                                            : <div className="promo-card-image promo-card-image--placeholder" />
                                        }
                                        <div className="promo-card-overlay">
                                            <h3 className="promo-card-title">{promo.promotionName}</h3>
                                            {promo.description && <p className="promo-card-desc">{promo.description}</p>}
                                            <button
                                                className={`promo-code-box ${copiedKey === key ? 'copied' : ''}`}
                                                onClick={() => handleCopy(key, promo.promoCode)}
                                                title="Click to copy promo code"
                                            >
                                                <TagIcon />
                                                <span className="promo-code">{promo.promoCode}</span>
                                                <span className="copy-divider" />
                                                <CopyIcon copied={copiedKey === key} />
                                                <span className="copy-label">{copiedKey === key ? 'Copied!' : 'Copy'}</span>
                                            </button>
                                        </div>
                                    </div>
                                    {promo.endDate && (
                                        <p className="promo-valid">Valid until: {formatEndDate(promo.endDate)}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {promoCards.length > 0 && (
                    <div className="promo-carousel-wrapper">
                        <div className="promo-carousel" ref={sliderRef} onScroll={handleSliderScroll}>
                            {promoCards.map((promo) => {
                                const key = `promo-mobile-${promo.id}`;
                                return (
                                    <div key={promo.id} className="promo-carousel-slide">
                                        <div className="promo-card">
                                            {promo.imageUrl
                                                ? <img src={promo.imageUrl} alt={promo.promotionName} className="promo-card-image" />
                                                : <div className="promo-card-image promo-card-image--placeholder" />
                                            }
                                            <div className="promo-card-overlay">
                                                <h3 className="promo-card-title">{promo.promotionName}</h3>
                                                {promo.description && <p className="promo-card-desc">{promo.description}</p>}
                                                <button
                                                    className={`promo-code-box ${copiedKey === key ? 'copied' : ''}`}
                                                    onClick={() => handleCopy(key, promo.promoCode)}
                                                    title="Click to copy promo code"
                                                >
                                                    <TagIcon />
                                                    <span className="promo-code">{promo.promoCode}</span>
                                                    <span className="copy-divider" />
                                                    <CopyIcon copied={copiedKey === key} />
                                                    <span className="copy-label">{copiedKey === key ? 'Copied!' : 'Copy'}</span>
                                                </button>
                                            </div>
                                        </div>
                                        {promo.endDate && (
                                            <p className="promo-valid">Valid until: {formatEndDate(promo.endDate)}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="promo-dots">
                            {promoCards.map((_, idx) => (
                                <button
                                    key={idx}
                                    className={`promo-dot ${activeSlide === idx ? 'active' : ''}`}
                                    onClick={() => handleDotClick(idx)}
                                    aria-label={`Go to slide ${idx + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <section className="category-deals-section">
                <div className="promos-section-header">
                    <h2 className="promos-section-title">Category Deals</h2>
                    <p className="promos-section-sub">
                        Discover exclusive discounts on selected products and categories.
                    </p>
                </div>

                <div className="promos-filter-row">
                    {CATEGORIES.map((category) => (
                        <button
                            key={category}
                            className={`promos-filter-btn ${activeCategory === category ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category)}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                <div className="deals-grid">
                    {categoryDeals.length > 0 ? (
                        categoryDeals.map((deal) => {
                            const key = `deal-${deal.id}`;
                            return (
                                <div key={deal.id} className="deal-card">
                                    {deal.imageUrl
                                        ? <img src={deal.imageUrl} alt={deal.promotionName} className="deal-card-image" />
                                        : <div className="deal-card-image deal-card-image--placeholder" />
                                    }
                                    <div className="deal-overlay">
                                        <div className="deal-content">
                                            <h3 className="deal-title">
                                                {deal.discountValue
                                                    ? `${deal.promotionType === 'fixed' ? 'P' : ''}${deal.discountValue}${deal.promotionType === 'percentage' ? '%' : ''} OFF`
                                                    : deal.promotionName}
                                            </h3>
                                            {deal.description && <p className="deal-subtitle">{deal.description}</p>}
                                            {deal.promoCode && (
                                                <button
                                                    className={`deal-code ${copiedKey === key ? 'copied' : ''}`}
                                                    onClick={() => handleCopy(key, deal.promoCode)}
                                                    title="Click to copy promo code"
                                                >
                                                    <TagIcon />
                                                    <span>{deal.promoCode}</span>
                                                    <span className="copy-divider" />
                                                    <CopyIcon copied={copiedKey === key} />
                                                    <span className="copy-label">{copiedKey === key ? 'Copied!' : 'Copy'}</span>
                                                </button>
                                            )}
                                            <button className="shop-now-btn" onClick={() => navigate('/browse')}>
                                                Shop Now
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-deals-notice">
                            <NoDealsIcon />
                            <h3>No deals available</h3>
                            <p>There are currently no active promotions for <strong>{activeCategory}</strong>. Check back soon!</p>
                        </div>
                    )}
                </div>
            </section>

            <p className="footer-note">
                (i) Promos are valid while supplies last and subject to change without notice.
                Terms and conditions apply. For more details, visit our FAQs or Support page.
            </p>
        </div>
    );
};

function TagIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0, pointerEvents: 'none', userSelect: 'none' }}
        >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
    );
}

function CopyIcon({ copied }) {
    return copied ? (
        <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0, pointerEvents: 'none', userSelect: 'none' }}
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ) : (
        <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0, pointerEvents: 'none', userSelect: 'none' }}
        >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
    );
}

function NoDealsIcon() {
    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ccc"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );
}

export default Promotions;